import { WorkspaceRole } from '../../auth/domain/account';
import { Role, RoleKey, RoleNameMap } from '../hooks/useRoles';

/**
 * Whether the caller's resolved role in this workspace is Guest.
 *
 * A bare predicate: `false` means "not provably Guest", which includes the
 * case where the role list has not arrived. Do NOT use it on its own to decide
 * whether to show a workspace — go through `getVisibleWorkspaces`, which owns
 * the unresolved-data policy. This is presentation only; it never reads or
 * implies anything about membership.
 */
export const isGuestWorkspaceRole = (
  workspaceId: string,
  workspaceRoles: WorkspaceRole[] | undefined,
  roles: Role[] | undefined
): boolean => {
  if (!roles || roles.length < 1) return false;

  const workspaceRole = workspaceRoles?.find(
    ({ workspace }) => workspace === workspaceId
  );
  if (!workspaceRole) return false;

  const roleName = roles.find(({ id }) => id === workspaceRole.role)?.name;
  return roleName === RoleNameMap[RoleKey.GUEST];
};

/**
 * Whether the caller holds a Guest role in ANY workspace they belong to, not
 * just the one they are currently switched into.
 *
 * `account.roles` (and permission markers derived from it, like `*.guest`)
 * describe only the workspace `changeWorkspace` last switched the caller
 * into, and a guest opens their own asset page from states where that is
 * never the workspace holding their grant: My History (`roles: [General]`)
 * or their own personal workspace (`roles: [Workspace Admin]`). Both roles
 * hold `workspaceroutine.read` / `group.read` / `category.read`, so neither
 * state trips the permission proxy the way an active Guest role does — the
 * marker cannot tell a guest apart from an ordinary member here.
 * `workspaceRoles` is the per-workspace membership record and survives every
 * switch, so it is the one signal that still says "Guest" in both states.
 *
 * Deliberately account-wide rather than scoped to one workspace: a caller
 * shaping a query has no workspace id to scope by until the query names one
 * in its response. A real member who is also a Guest of some unrelated
 * workspace is the one false positive this admits — costing them some
 * routine/group/category data on their own asset — which is the accepted
 * side of a fail-closed choice; the other direction blanks a real guest's
 * page outright.
 */
export const isGuestInAnyWorkspace = (
  workspaceRoles: WorkspaceRole[] | undefined,
  roles: Role[] | undefined
): boolean => {
  if (!workspaceRoles || workspaceRoles.length < 1) return false;
  if (!roles || roles.length < 1) return false;

  const guestRoleId = roles.find(
    ({ name }) => name === RoleNameMap[RoleKey.GUEST]
  )?.id;
  if (!guestRoleId) return false;

  return workspaceRoles.some(({ role }) => role === guestRoleId);
};

/**
 * Whether the caller has a RESOLVED, positive membership in this workspace
 * under some real role other than Guest.
 *
 * `isGuestWorkspaceRole` returning `false` is not this predicate's complement:
 * it also covers "no workspaceRoles entry for this workspace at all" and
 * "roles not yet loaded", neither of which is evidence of membership. A
 * caller deciding whether to upgrade a trimmed guest response to the full one
 * needs proof of membership, not merely the absence of a Guest verdict.
 */
export const isConfirmedNonGuestMember = (
  workspaceId: string,
  workspaceRoles: WorkspaceRole[] | undefined,
  roles: Role[] | undefined
): boolean => {
  if (!roles || roles.length < 1) return false;

  const workspaceRole = workspaceRoles?.find(
    ({ workspace }) => workspace === workspaceId
  );
  if (!workspaceRole) return false;

  const roleName = roles.find(({ id }) => id === workspaceRole.role)?.name;
  return !!roleName && roleName !== RoleNameMap[RoleKey.GUEST];
};

/**
 * The workspaces a caller may be offered in switching UI: those whose resolved
 * role is not Guest — a guest should not learn the shape of an organisation
 * from having replied to one conversation.
 *
 * Fails CLOSED on unresolved role data. Without the roles list no workspace can
 * be shown *not* to be a Guest one, and showing it anyway is exactly the leak
 * this rule exists to prevent — a rule that stops applying when a query is slow
 * or fails is not a rule. Callers must also keep the list off-screen while the
 * roles query is in flight (its `isLoading`), so the normal case is a spinner
 * rather than a list that empties and refills.
 *
 * Note this cannot strand anyone: every switching surface renders a My History
 * row that does not come from this list, so the degraded state is still a
 * usable destination and a reload recovers.
 */
export const getVisibleWorkspaces = <T extends { id: string }>(
  allowedWorkspaces: T[] | undefined,
  workspaceRoles: WorkspaceRole[] | undefined,
  roles: Role[] | undefined
): T[] => {
  if (!allowedWorkspaces) return [];
  if (!roles || roles.length < 1) return [];

  return allowedWorkspaces.filter(
    (workspace) => !isGuestWorkspaceRole(workspace.id, workspaceRoles, roles)
  );
};
